import * as React from 'react';

import {
  DocumentType,
  enumDocumentType,
  enumPurchaseLoadSortOption,
  PurchaseLoadSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  useMemoDataGridRows,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideDownload, LucideX } from 'lucide-react';

import * as state from '../../_state/transactions.state';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { useTranslations } from '../../i18n/useTranslations';
import { InboundTransactionRecord } from '../../types/transaction';

import { useTransactionsInboundColumns } from './hooks/useTransactionsInboundColumns';
import { TransactionSidePanel } from './TransactionSidePanel';

type DocumentRequest = {
  documentType: DocumentType;
  loadId: string;
};

interface InboundSortProps {
  asc: PurchaseLoadSortOption;
  desc: PurchaseLoadSortOption;
}

const INBOUND_SORT_MAP: Record<string, InboundSortProps> = {
  documentNumber: {
    asc: enumPurchaseLoadSortOption.DocumentNumberAsc,
    desc: enumPurchaseLoadSortOption.DocumentNumberDesc,
  },
  receivedDate: {
    asc: enumPurchaseLoadSortOption.ReceivedDateAsc,
    desc: enumPurchaseLoadSortOption.ReceivedDateDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ TRANSACTIONS INBOUND DATA GRID ⚛️ ⚛️ ⚛️
//
export const TransactionsInboundDataGrid = () => {
  // Translations
  const t = useTranslations();

  const [sidePanelOpen, setSidePanelOpen] = React.useState(false);
  const [sidePanelDocLabel, setSidePanelDocLabel] = React.useState('');

  const setSelectedTransactionId = useSetAtom(state.$selectedTransactionId);
  const setInboundSorting = useSetAtom(state.$inboundSorting);
  const setOffset = useSetAtom(state.$offset);
  const hasFiltersActive = useAtomValue(state.$filtersActive);
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedInboundKeys);

  // Data hooks
  const {
    data: inboundData,
    isFetching,
    loadingState,
    error,
  } = useAtomValue(state.$remoteInbound);
  const { print, isPrinting } = usePrintWithBrowser();

  // Memos
  const tickets = inboundData?.purchaseLoads ?? [];

  const handleDoubleClick = React.useCallback(
    (row: InboundTransactionRecord) => {
      setSelectedTransactionId(row.id);
      setSidePanelDocLabel(String(row.documentNumber));
      setSidePanelOpen(true);
    },
    [setSelectedTransactionId],
  );

  const { columns, columnVisibilityState } = useTransactionsInboundColumns({
    onDoubleClick: handleDoubleClick,
  });

  const selectedRows = useMemoDataGridRows(tickets, selectedKeys);

  // Callbacks
  const handleColumnSort = React.useCallback(
    (columnId: string, sortDirection: 'ascending' | 'descending' | null) => {
      if (sortDirection === null) {
        setInboundSorting([enumPurchaseLoadSortOption.CreatedAtDesc]);
      } else {
        const sortMap = INBOUND_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            sortDirection === 'ascending' ? sortMap.asc : sortMap.desc;
          setInboundSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setInboundSorting, setOffset],
  );

  const handleBulkDownload = React.useCallback(async () => {
    const documents = selectedRows.flatMap((row) => {
      const docs: DocumentRequest[] = [
        { documentType: enumDocumentType.BillOfLading, loadId: row.id },
      ];
      if (row.purchaseOrderId) {
        docs.push({
          documentType: enumDocumentType.PurchaseOrder,
          loadId: row.id,
        });
      }
      return docs;
    });

    await print({ documents, action: 'download' });
    setSelectedKeys(new Set<React.Key>());
  }, [selectedRows, print, setSelectedKeys]);

  return (
    <>
      <DataGrid<InboundTransactionRecord>
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={tickets}
        rowKey="id"
        aria-label={t.transactionsView.table.aria}
        selectionMode="multiple"
        onSelectionChange={setSelectedKeys}
        onColumnSort={handleColumnSort}
        columnVisibilityState={columnVisibilityState}
        className="w-full text-sm"
        fullWidth
        persistColumnOrderKey="trade:transactions-inbound"
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.transactionsView.content.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={
          hasFiltersActive
            ? {
                title: t.transactionsView.emptyResultsState.title,
                description: t.transactionsView.emptyResultsState.subtitle,
                centered: true,
              }
            : {
                title: t.transactionsView.emptyState.title,
                description: t.transactionsView.emptyState.subtitle,
                centered: true,
              }
        }
      />
      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.transactionsView.toolbar.selected}
          </div>
          <Button
            variant="outline"
            icon={<LucideDownload />}
            isDisabled={selectedRows.length < 1 || isPrinting}
            isLoading={isPrinting}
            onPress={handleBulkDownload}
          >
            {t.transactionsView.toolbar.downloadDocuments(selectedRows.length)}
          </Button>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={() => setSelectedKeys(new Set<React.Key>())}
          />
        </div>
      </FloatingToolbar>
      <TransactionSidePanel
        direction="inbound"
        documentLabel={sidePanelDocLabel}
        isOpen={sidePanelOpen}
        onOpenChange={setSidePanelOpen}
      />
    </>
  );
};
